// Extend Window for boot markers
declare global {
  interface Window {
    __APP_BOOTED__?: boolean;
    __MAIN_IMPORTED__?: boolean;
    __APP_RENDER_STARTED__?: boolean;
    __MODULE_LOADED__?: boolean;
  }
}

// Module script executed — this confirms Vite delivered the module
// build: force-redeploy 2026-06-03b (local ThemeProvider replaces next-themes)
window.__MODULE_LOADED__ = true;

// Early error capture — catches import/eval failures before React mounts
window.__APP_BOOTED__ = false;
window.addEventListener('error', (e) => {
  console.error('[boot] Uncaught error:', e.message, e.filename, e.lineno);
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[boot] Unhandled rejection:', e.reason);
});

console.log('[main] start');
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { preloadPrerenderedRoute } from "@/lib/prerenderedRoutes";
import { warmAppChunksIdle } from "@/lib/prerenderedAppChunks";
import { warmDeferredHomeChromeIdle } from "@/lib/prerenderedHomeChunks";

// Stage 1: all modules imported successfully
window.__MAIN_IMPORTED__ = true;
console.log('[main] modules imported');

const rootEl = document.getElementById("root")!;

// Detect prerendered marketing snapshot HTML. These pages are captured by a
// Playwright/browser CSR snapshot in scripts/prerender-marketing.mjs — they
// are NOT true React SSR output, so `hydrateRoot` cannot adopt them
// reliably (React #418/#423). For snapshot pages we keep the static HTML
// visible, preload the matched route chunks, then mount fresh with
// `createRoot` in a single controlled commit. True SSR output (none today)
// would set a different marker and use hydrateRoot.
const isSnapshotPrerender =
  rootEl.getAttribute("data-prerender-mode") === "snapshot";

function finishBoot() {
  // Stage 3: render() returned — React tree is mounting
  window.__APP_BOOTED__ = true;
  console.log("[main] render() called, boot marker set");

  // Dismiss retry banner if it appeared before boot completed
  const retryBanner = document.getElementById("boot-retry-banner");
  if (retryBanner) retryBanner.remove();
}

async function boot() {
  // Stage 2: about to call render
  window.__APP_RENDER_STARTED__ = true;

  const tree = (
    <StrictMode>
      <App />
    </StrictMode>
  );

  if (isSnapshotPrerender) {
    // Preload the public shell + matched route module so the very first
    // client render resolves synchronously with no Suspense fallback,
    // leaving the static snapshot visually intact until the React tree
    // commits in one frame.
    try {
      await preloadPrerenderedRoute(window.location.pathname);
    } catch (err) {
      console.warn("[main] snapshot route preload failed, continuing", err);
    }
    console.log("[main] mounting over snapshot root");
    // createRoot replaces the snapshot in a single commit. The snapshot
    // stays visually painted until React's first commit, so users do not
    // see a blank flash. We do NOT call hydrateRoot here — the snapshot
    // is browser-captured CSR DOM and is not a valid hydration source.
    createRoot(rootEl).render(tree);
    finishBoot();
    // Warm deferred chunks (toaster, sonner, cookie consent banner,
    // maintenance banner) during browser idle so the first interaction
    // that needs them is instant. Does not block boot.
    warmAppChunksIdle();
    warmDeferredHomeChromeIdle();
    return;
  }

  console.log("[main] rendering empty root");
  createRoot(rootEl).render(tree);
  finishBoot();
  warmAppChunksIdle();
  warmDeferredHomeChromeIdle();
}

void boot();
